import { BarChart3 } from "lucide-react";
import { useEffect, useState } from "react";
import { ANALYTICS_COPY } from "../copy/disclaimers";
import {
  ANALYTICS_CONSENT_CHANGED_EVENT,
  getAnalyticsConsentSnapshot,
  setAnalyticsConsentChoice,
  type AnalyticsConsentSnapshot,
  type PublicAnalyticsConsentChoice
} from "../services/analytics/analyticsConfig";
import { trackAnalyticsEvent } from "../services/analytics/analyticsClient";
import { clearAnalyticsInstallId } from "../services/analytics/analyticsContext";
import { flushAnalyticsQueue } from "../services/analytics/analyticsFlush";
import { clearAnalyticsQueue } from "../services/analytics/analyticsQueueRepo";

export function AnalyticsConsentCard(): JSX.Element | null {
  const snapshot = useAnalyticsConsentSnapshot();
  if (!snapshot.shouldRequestChoice) {
    return null;
  }

  return (
    <div className="analytics-consent" role="region" aria-label="Optional analytics choice">
      <div className="analytics-consent__icon" aria-hidden="true">
        <BarChart3 size={16} />
      </div>
      <div className="analytics-consent__content">
        <div className="analytics-consent__copy">
          <h2>Optional analytics</h2>
          <p>Reliability stats only. No photos, notes, audio, addresses, room names, files, or PDFs.</p>
        </div>
        <div className="analytics-consent__actions">
          <button className="button button--primary" type="button" onClick={() => chooseAnalyticsConsent("allowed")}>
            Allow
          </button>
          <button className="button button--quiet" type="button" onClick={() => chooseAnalyticsConsent("declined")}>
            Not now
          </button>
          <a className="analytics-consent__link" href="#privacy-details">
            Details
          </a>
        </div>
      </div>
    </div>
  );
}

export function AnalyticsPreferenceControls(): JSX.Element | null {
  const snapshot = useAnalyticsConsentSnapshot();
  if (!snapshot.isPublicAnalyticsAvailable && !snapshot.isInternalQa) {
    return null;
  }

  return (
    <div className="analytics-preference" aria-label="Optional analytics preference">
      <div>
        <strong>Optional analytics</strong>
        <p>{statusCopy(snapshot)}</p>
      </div>
      {!snapshot.isInternalQa && (
        <div className="analytics-preference__actions">
          <button
            className="button button--primary"
            type="button"
            disabled={snapshot.analyticsConsentState === "allowed" && snapshot.canSendOptionalAnalytics}
            onClick={() => chooseAnalyticsConsent("allowed")}
          >
            Allow optional analytics
          </button>
          <button
            className="button"
            type="button"
            disabled={snapshot.analyticsConsentState === "declined"}
            onClick={() => chooseAnalyticsConsent("declined")}
          >
            Not now
          </button>
        </div>
      )}
    </div>
  );
}

function useAnalyticsConsentSnapshot(): AnalyticsConsentSnapshot {
  const [snapshot, setSnapshot] = useState<AnalyticsConsentSnapshot>(() => getAnalyticsConsentSnapshot());

  useEffect(() => {
    const sync = () => setSnapshot(getAnalyticsConsentSnapshot());
    window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return snapshot;
}

function chooseAnalyticsConsent(choice: PublicAnalyticsConsentChoice): void {
  setAnalyticsConsentChoice(choice);
  if (choice === "declined") {
    clearAnalyticsInstallId();
    void clearAnalyticsQueue();
    return;
  }
  trackAnalyticsEvent("app_open", {}, { flush: true });
  void flushAnalyticsQueue("milestone");
}

function statusCopy(snapshot: AnalyticsConsentSnapshot): string {
  if (snapshot.isInternalQa) {
    return "Internal QA analytics is enabled for this browser.";
  }
  if (snapshot.canSendOptionalAnalytics) {
    return "Allowed for this browser. You can turn it off without affecting core app features.";
  }
  if (snapshot.analyticsConsentState === "declined") {
    return "Off for this browser. Core app features still work.";
  }
  return ANALYTICS_COPY;
}
